/**
 * A dashboard's filters, in and out of its URL.
 *
 * The URL is where a dashboard's filters are LOADED from and SAVED to — that
 * is what makes a filtered view bookmarkable and shareable, and what keeps a
 * reader's filter from ever being saved for everybody. So this is the
 * dashboard's half of the shared filter bar's contract: the bar hands back
 * rules, and these turn them into a link and back.
 *
 * `field:key,key` for `is any of`, exactly as links were written before
 * filters had operators, so every link already shared still opens filtered.
 * Any other operator is `field~op:value`, the value being the list for
 * `is none of` and JSON otherwise. `|` between rules, in bar order.
 *
 * Hand-rolled rather than JSON-in-a-query-param so the common case stays
 * something a person can read and edit. Anything malformed is DROPPED rather
 * than thrown: a shared link that picked up a stray character should open the
 * dashboard unfiltered, not refuse to open at all.
 */
import {
  FILTER_OPS,
  type FilterNode,
  type FilterOp,
  type FilterRule,
  type FilterValue,
  filterValueSchema,
  OP_ARITY,
} from '@clockwise/semantic-core';
import { saysSomething } from './filters';

const LIST_OPS: readonly FilterOp[] = ['in', 'not_in'];

/**
 * Rules out of a URL.
 *
 * Ids are made up here — `field`, then `field-2` for a second rule on the same
 * column — because a link does not carry them. They are stable for a given
 * link, which is all a rule's id has to be.
 */
export function parseFilters(encoded?: string): FilterRule[] {
  if (!encoded) return [];
  const out: FilterRule[] = [];
  const perField = new Map<string, number>();

  for (const part of encoded.split('|')) {
    const at = part.indexOf(':');
    if (at <= 0) continue;
    const head = safeDecode(part.slice(0, at));
    const payload = part.slice(at + 1);
    if (head === null) continue;

    const tilde = head.indexOf('~');
    const field = tilde < 0 ? head : head.slice(0, tilde);
    const op = (tilde < 0 ? 'in' : head.slice(tilde + 1)) as FilterOp;
    if (!field || !(FILTER_OPS as readonly string[]).includes(op)) continue;

    const value = parseValue(op, payload);
    if (value === null) continue;

    const n = (perField.get(field) ?? 0) + 1;
    perField.set(field, n);
    out.push({ id: n === 1 ? field : `${field}-${n}`, field, op, value, paused: false });
  }
  return out;
}

/** `undefined` for an operator that takes no value; `null` for a part to drop. */
function parseValue(op: FilterOp, payload: string): FilterValue | undefined | null {
  if (LIST_OPS.includes(op)) {
    const values = payload
      .split(',')
      .filter(Boolean)
      .map(safeDecode)
      .filter((v): v is string => v !== null);
    return values.length > 0 ? { kind: 'list', values } : null;
  }
  if (OP_ARITY[op] === 'none') return payload ? null : undefined;
  const text = safeDecode(payload);
  if (!text) return null;
  try {
    const parsed = filterValueSchema.safeParse(JSON.parse(text));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Rules into a URL — only the ones that SAY something.
 *
 * A chip still waiting for its first value, a paused rule, a group and a
 * range open at both ends are all left out: none of them narrows anything.
 * `undefined` when nothing is left, so the parameter disappears.
 */
export function encodeFilters(filters: readonly FilterNode[]): string | undefined {
  const parts: string[] = [];
  // The same test the Clear button and the tiles apply — see `saysSomething`.
  for (const rule of filters.filter(saysSomething)) {
    const field = encodeURIComponent(rule.field);
    const head = rule.op === 'in' ? field : `${field}~${rule.op}`;
    const value = rule.value;
    if (value?.kind === 'list') {
      parts.push(`${head}:${value.values.map(encodeURIComponent).join(',')}`);
    } else {
      parts.push(`${head}:${value ? encodeURIComponent(JSON.stringify(value)) : ''}`);
    }
  }
  return parts.length > 0 ? parts.join('|') : undefined;
}

function safeDecode(text: string): string | null {
  try {
    return decodeURIComponent(text);
  } catch {
    return null;
  }
}
